import { useMemo, useState } from "react";
import {
  Box,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { AppTextField } from "../form";
import SearchIcon from "@mui/icons-material/Search";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import { useTranslation } from "react-i18next";
import type { Widget } from "../../types/widget";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

export const WIDGET_DRAG_MIME = "application/x-dashboard-widget-id";

type Props = {
  widgets: Widget[];
  placedWidgetIds: Set<string>;
  onAddWidget: (widgetId: string) => void;
  /** When true, renders list only (no outer Paper) for embedding in DesignSidebar. */
  embedded?: boolean;
};

export default function WidgetPalette({ widgets, placedWidgetIds, onAddWidget, embedded = false }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.widgetPalette;
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return widgets;
    return widgets.filter((w) => (w.name || "").toLowerCase().includes(q));
  }, [query, widgets]);

  const availableCount = filtered.filter((w) => w.id && !placedWidgetIds.has(w.id)).length;

  const listBody = (
    <>
      {!embedded && (
        <Box sx={styles.header}>
          <Stack direction="row" alignItems="center" spacing={1} sx={styles.headerTitleRow}>
            <DashboardCustomizeOutlinedIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={700} sx={styles.headerTitle}>
              {t("dashboard.palette")}
            </Typography>
            <Chip label={availableCount} size="small" color="primary" variant="outlined" />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t("dashboard.paletteHint")}
          </Typography>
        </Box>
      )}

      <Box sx={styles.listPadding}>
        <AppTextField
          placeholder={t("dashboard.paletteSearch")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={styles.search}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={styles.list}>
          {filtered.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={styles.empty}>
              {t("dashboard.paletteEmpty")}
            </Typography>
          )}
          {filtered.map((w) => {
            const id = w.id || "";
            const alreadyPlaced = placedWidgetIds.has(id);
            return (
              <Box
                key={id}
                draggable={!alreadyPlaced && !!id}
                onDragStart={(e) => {
                  if (!id || alreadyPlaced) {
                    e.preventDefault();
                    return;
                  }
                  e.dataTransfer.setData(WIDGET_DRAG_MIME, id);
                  e.dataTransfer.effectAllowed = "copy";
                }}
                onDoubleClick={() => {
                  if (id && !alreadyPlaced) onAddWidget(id);
                }}
                sx={styles.item(alreadyPlaced)}
              >
                <DragIndicatorIcon fontSize="small" color={alreadyPlaced ? "disabled" : "action"} />
                <Box sx={styles.textBlock}>
                  <Typography variant="body2" noWrap sx={styles.itemTitle}>
                    {w.name || id.slice(0, 8)}
                  </Typography>
                  {alreadyPlaced && (
                    <Chip
                      label={t("dashboard.alreadyOnDashboard")}
                      size="small"
                      sx={styles.placedChip}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );

  if (embedded) {
    return (
      <Box sx={styles.embedded}>
        {listBody}
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={styles.paper}
    >
      {listBody}
    </Paper>
  );
}
